import { motion } from "framer-motion";
import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { MapPin } from "lucide-react";
import {
  BASHO_INTRO,
  BASHO_BIO,
  BASHO_TIMELINE,
  BASHO_HAIKU,
} from "../data/basho";
import AntiqueMap from "../components/AntiqueMap";

const MOUNTAIN = "https://customer-assets-lxgj4vgw.emergentagent.net/job_renga-rooms/artifacts/5oup2gs7_Basho%27s%20Life.JPEG";

const Reveal = ({ children, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

export default function Basho() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }, [location]);

  return (
    <div data-testid="basho-page">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-16 grid md:grid-cols-12 gap-10 items-end">
        <div className="md:col-span-7">
          <span className="hanko text-primary text-3xl">{BASHO_INTRO.japanese}</span>
          <h1 className="font-serif font-light text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tighter mt-4 leading-[0.95]">
            {BASHO_INTRO.name}
          </h1>
          <p className="mt-4 text-sm tracking-[0.25em] uppercase text-secondary">
            {BASHO_INTRO.years}
          </p>
          <p className="mt-8 max-w-xl text-lg text-muted-foreground leading-relaxed italic font-serif">
            {BASHO_INTRO.tagline}
          </p>
        </div>
        <div className="md:col-span-5">
          <img src={MOUNTAIN} alt="The Traveling Poet — ink portrait of Matsuo Bashō" className="w-full h-80 object-contain bg-muted/30 border border-border" />
        </div>
      </section>

      {/* BIOGRAPHY */}
      <section className="border-y border-border bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight sticky top-24">
              A life<br />in wandering
            </h2>
          </div>
          <div className="md:col-span-8 space-y-8">
            {BASHO_BIO.map((p, i) => (
              <Reveal key={`bio-${i}`}>
                <p className="text-lg leading-relaxed text-foreground/90">{p}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight mb-16">
          The road, year by year
        </h2>
        <div className="grid md:grid-cols-12 gap-8 items-start">
          <div className="md:col-span-5 md:sticky md:top-24">
            <AntiqueMap to="/map" />
            <Link to="/map" data-testid="map-caption-link"
              className="mt-3 inline-flex items-center gap-1.5 text-sm text-muted-foreground italic hover:text-primary transition-colors">
              <MapPin className="w-3.5 h-3.5" /> The narrow road to the deep north — trace the journey
            </Link>
          </div>
          <div className="md:col-span-7">
            <div className="divide-y divide-border border-t border-border">
              {BASHO_TIMELINE.map((t) => {
                const isJourney = t.year === "1689";
                return (
                  <Reveal key={t.year}>
                    <div id={`life-${t.year}`} className="py-6 grid grid-cols-12 gap-4 group hover:bg-muted/40 transition-colors px-2 scroll-mt-24">
                      <span className="col-span-3 font-serif text-2xl text-primary">{t.year}</span>
                      <div className="col-span-9">
                        <h3 className="font-serif text-xl">{t.title}</h3>
                        <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{t.text}</p>
                        {isJourney && (
                          <Link to="/map" data-testid="timeline-map-link"
                            className="mt-2 inline-flex items-center gap-1.5 text-sm text-secondary hover:text-primary transition-colors">
                            <MapPin className="w-3.5 h-3.5" /> View the road on the map
                          </Link>
                        )}
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* FAMOUS HAIKU */}
      <section className="border-t border-border bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight mb-16">
            Verses that endure
          </h2>
          <div className="grid md:grid-cols-2 gap-px bg-border border border-border">
            {BASHO_HAIKU.map((h) => (
              <Reveal key={h.romaji} className="bg-background">
                <div className="p-7 sm:p-10 md:p-12 h-full flex gap-5 sm:gap-8 hover:bg-muted/40 transition-colors">
                  <p className="font-poem writing-vertical text-2xl leading-loose text-primary whitespace-pre-line">
                    {h.jp}
                  </p>
                  <div className="flex-1">
                    <p className="font-poem text-xl leading-relaxed whitespace-pre-line">{h.en}</p>
                    <p className="mt-5 italic font-serif text-muted-foreground">{h.romaji}</p>
                    <p className="mt-3 text-xs tracking-widest uppercase text-secondary">{h.note}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
